import React, { useState, useEffect } from 'react';
import { useTranslation } from '../../context/TranslationContext';
import { Button } from '../';
import MessageLog from '../MessageLog';
import DynamicSelectField from './DynamicSelectField';
import debug from '@commons-shared/debug';
import { get, post, put, del } from '../../utils/api-router';
import { useMessageLog } from '../../hooks/useMessageLog';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

/**
 * Fonction helper pour rendre les icônes de toggle
 */
const renderBooleanToggle = (value, field) => {
  const isActive = Boolean(value);
  
  // Si la colonne a un labelIcon, l'utiliser au lieu du toggle
  if (field.labelIcon) {
    // Si inactive (toggle off), afficher un espace vide
    if (!isActive) {
      return <span>&nbsp;</span>;
    }
    
    // Si active, afficher l'icône en vert
    const colorStyle = {
      color: 'var(--color-success)'
    };
    
    return (
      <FontAwesomeIcon 
        icon={field.labelIcon}
        style={colorStyle}
        title={`${field.label}: Actif`}
      />
    );
  }
  
  // Sinon, utiliser les icônes de toggle traditionnelles
  const iconClass = isActive ? 'fas fa-toggle-on' : 'fas fa-toggle-off';
  const colorStyle = {
    color: isActive ? 'var(--color-success)' : 'var(--color-error)'
  };
  
  return (
    <i 
      className={iconClass} 
      style={colorStyle}
      title={field?.label || (isActive ? 'Actif' : 'Inactif')}
      aria-hidden="true"
    />
  );
};

/**
 * Composant générique d'administration de données
 * @param {Object} config - Configuration du composant
 * @param {string} config.entityName - Nom de l'entité (ex: 'translations', 'ia-models')
 * @param {string} config.apiEndpoint - Point d'API de base (ex: '/api/config/tool/translations')
 * @param {Array} config.columns - Définition des colonnes du tableau
 * @param {Array} config.formFields - Définition des champs du formulaire
 * @param {Array} config.filters - Définition des filtres
 * @param {Object} config.defaultFormData - Données par défaut du formulaire
 * @param {Object} config.translations - Clés de traduction spécifiques
 * @param {string} config.exportFileName - Nom de base pour les fichiers d'export
 * @param {boolean} config.hasPagination - Active la pagination (défaut: true)
 * @param {boolean} config.hasFilters - Active les filtres (défaut: true)
 * @param {Function} config.validateForm - Fonction de validation du formulaire (optionnel)
 * @param {Function} config.transformDataForApi - Transformation des données avant envoi API (optionnel)
 * @param {Function} config.transformDataFromApi - Transformation des données après réception API (optionnel)
 */
export default function AdminDataTable({
  entityName,
  apiEndpoint,
  columns = [],
  formFields = [],
  filters: filterDefinitions = [],
  defaultFormData = {},
  translations = {},
  exportFileName = 'data',
  hasPagination = true,
  hasFilters = true,
  validateForm = null,
  transformDataForApi = null,
  transformDataFromApi = null
}) {
  const { t } = useTranslation();
  const { message, showError, showSuccess, showInfo, showWarning } = useMessageLog({ autoHide: true, duration: 5000 });
  
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState(defaultFormData);
  
  // États pour les filtres
  const [filters, setFilters] = useState(() => {
    const initialFilters = { limit: 50, offset: 0 };
    filterDefinitions.forEach(filter => {
      initialFilters[filter.name] = filter.defaultValue || '';
    });
    return initialFilters;
  });
  
  // État pour le total des résultats
  const [totalCount, setTotalCount] = useState(0);

  // Charger les données avec les filtres actuels
  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Construire l'URL avec les paramètres de filtrage
      let url = `${apiEndpoint}?format=json`;
      Object.entries(filters).forEach(([key, value]) => {
        if (value) url += `&${key}=${encodeURIComponent(value)}`;
      });
      
      const response = await get(url);
      
      if (response.success) {
        const items = response[entityName] || response.data || [];
        const transformedItems = transformDataFromApi ? transformDataFromApi(items) : items;
        setData(transformedItems);
        
        // L'API retourne "total", pas "totalCount"
        const count = response.total !== undefined ? response.total : transformedItems.length;
        setTotalCount(count);
        
        debug.log(`Admin${entityName}`, `${transformedItems.length} éléments chargés`, {
          totalCount: count,
          fromAPI: response.total !== undefined
        });
      } else {
        throw new Error(response.error || `Erreur lors du chargement des ${entityName}`);
      }
    } catch (err) {
      setError(err.message);
      debug.error(`Admin${entityName}`, `Erreur lors du chargement des ${entityName}`, err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [filters]);

  // Gérer le changement des filtres
  const handleFilterChange = (e) => {
    const { name, value, type, checked } = e.target;
    const filterValue = type === 'checkbox' ? (checked ? '1' : '0') : value;
    
    setFilters(prev => ({
      ...prev,
      [name]: filterValue,
      // Réinitialiser l'offset lors d'un changement de filtre
      ...(name !== 'offset' && name !== 'limit' ? { offset: 0 } : {})
    }));
  };

  // Gérer la pagination
  const handlePageChange = (direction) => {
    if (direction === 'next' && filters.offset + filters.limit < totalCount) {
      setFilters(prev => ({
        ...prev,
        offset: prev.offset + prev.limit
      }));
    } else if (direction === 'prev' && filters.offset > 0) {
      setFilters(prev => ({
        ...prev,
        offset: Math.max(0, prev.offset - prev.limit)
      }));
    }
  };

  // Gérer le changement dans le formulaire
  const handleFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  // Soumettre le formulaire (ajouter ou modifier)
  const handleSubmitForm = async (e) => {
    e.preventDefault();
    
    // Validation du formulaire si fournie
    if (validateForm) {
      const validationError = validateForm(formData);
      if (validationError) {
        showError(validationError);
        return;
      }
    }
    
    try {
      let response;
      const dataToSend = transformDataForApi ? transformDataForApi(formData) : formData;
      
      if (editingId) {
        // Mise à jour d'un élément existant
        response = await put(`${apiEndpoint}/${editingId}`, dataToSend);
        
        if (response.success) {
          showSuccess(t('ADMIN', 'ITEM_UPDATED', 'Élément mis à jour avec succès'));
          
          // Mettre à jour la liste
          setData(prev => 
            prev.map(item => item.id === editingId ? { ...item, ...dataToSend } : item)
          );
        } else {
          throw new Error(response.error || 'Erreur lors de la mise à jour');
        }
      } else {
        // Ajout d'un nouvel élément
        response = await post(apiEndpoint, dataToSend);
        
        if (response.success) {
          showSuccess(t('ADMIN', 'ITEM_ADDED', 'Élément ajouté avec succès'));
          
          // Recharger les données
          setFilters(prev => ({ ...prev }));
        } else {
          throw new Error(response.error || 'Erreur lors de l\'ajout');
        }
      }
      
      // Réinitialiser le formulaire et fermer la fenêtre
      setFormData(defaultFormData);
      setEditingId(null);
      setShowForm(false);
      
    } catch (err) {
      showError(`Erreur lors de la ${editingId ? 'mise à jour' : 'création'} : ${err.message}`);
    }
  };

  // Ouvrir le formulaire pour ajouter un nouvel élément
  const handleAddItem = () => {
    setFormData(defaultFormData);
    setEditingId(null);
    setShowForm(true);
  };

  // Ouvrir le formulaire pour éditer un élément existant
  const handleEditItem = (item) => {
    setFormData({ ...item });
    setEditingId(item.id);
    setShowForm(true);
  };

  // Annuler le formulaire
  const handleCancelForm = () => {
    setShowForm(false);
    setEditingId(null);
    setFormData(defaultFormData);
  };

  // Supprimer un élément
  const handleDeleteItem = async (id) => {
    if (!window.confirm(t('ADMIN', 'CONFIRM_DELETE', 'Êtes-vous sûr de vouloir supprimer cet élément ?'))) {
      return;
    }
    
    try {
      const response = await del(`${apiEndpoint}/${id}`);
      
      if (response.success) {
        showSuccess(t('ADMIN', 'ITEM_DELETED', 'Élément supprimé avec succès'));

        // Mettre à jour la liste
        setData(prev => prev.filter(item => item.id !== id));
        
        // Mettre à jour le compteur total
        setTotalCount(prev => prev - 1);
      } else {
        throw new Error(response.error || 'Erreur lors de la suppression');
      }
    } catch (err) {
      showError(`Erreur lors de la suppression : ${err.message}`);
    }
  };

  // Exporter au format CSV
  const handleExportCsv = async () => {
    try {
      setLoading(true);
      
      // Récupérer les données en JSON pour une conversion côté client plus fiable
      let apiUrl = `${apiEndpoint}?format=json`;
      Object.entries(filters).forEach(([key, value]) => {
        if (value && key !== 'limit' && key !== 'offset') {
          apiUrl += `&${key}=${encodeURIComponent(value)}`;
        }
      });
      
      const response = await get(apiUrl);
      
      if (!response.success || !response[entityName] || !Array.isArray(response[entityName])) {
        throw new Error('Erreur lors de la récupération des données');
      }
      
      const items = response[entityName];
      
      // Générer manuellement le contenu CSV
      const headers = columns.map(col => col.key).join(',');
      let csvContent = headers + '\n';
      
      items.forEach(item => {
        const row = columns.map(col => {
          const value = item[col.key];
          // Échapper les champs texte qui pourraient contenir des virgules ou des guillemets
          if (value === null || value === undefined) return '';
          return `"${String(value).replace(/"/g, '""')}"`;
        });
        csvContent += row.join(',') + '\n';
      });
      
      // Créer un objet Blob et un lien de téléchargement
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8' });
      const downloadUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.setAttribute('download', `${exportFileName}.csv`);
      document.body.appendChild(link);
      
      // Déclencher le téléchargement
      link.click();
      
      // Nettoyer
      document.body.removeChild(link);
      URL.revokeObjectURL(downloadUrl);

      showSuccess(t('ADMIN', 'EXPORT_SUCCESS', 'Export CSV réussi'));
    } catch (err) {
      showError(`Erreur lors de l'exportation CSV : ${err.message}`);
    } finally {
      setLoading(false);
    }
  };
  
  // Exporter au format JSON
  const handleExportJson = async () => {
    try {
      setLoading(true);
      
      // Récupérer les données en JSON
      let apiUrl = `${apiEndpoint}?format=json`;
      Object.entries(filters).forEach(([key, value]) => {
        if (value && key !== 'limit' && key !== 'offset') {
          apiUrl += `&${key}=${encodeURIComponent(value)}`;
        }
      });
      
      const response = await get(apiUrl);
      
      if (!response.success || !response[entityName] || !Array.isArray(response[entityName])) {
        throw new Error('Erreur lors de la récupération des données');
      }
      
      // Créer une copie propre des données pour l'export
      const exportData = {
        [entityName]: response[entityName],
        exported_at: new Date().toISOString(),
        count: response[entityName].length,
        filters: Object.fromEntries(
          filterDefinitions.map(filter => [
            filter.name, 
            filters[filter.name] || 'all'
          ])
        )
      };
      
      // Créer un objet Blob et un lien de téléchargement
      const jsonContent = JSON.stringify(exportData, null, 2);
      const blob = new Blob([jsonContent], { type: 'application/json;charset=utf-8' });
      const downloadUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.setAttribute('download', `${exportFileName}.json`);
      document.body.appendChild(link);
      
      // Déclencher le téléchargement
      link.click();
      
      // Nettoyer
      document.body.removeChild(link);
      URL.revokeObjectURL(downloadUrl);

      showSuccess(t('ADMIN', 'EXPORT_JSON_SUCCESS', 'Export JSON réussi'));
    } catch (err) {
      showError(`Erreur lors de l'exportation JSON : ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Rendu du formulaire
  const renderForm = () => {
    if (!showForm) return null;

    return (
      <div className="wa-form-container">
        <h3>
          {editingId 
            ? t('ADMIN', 'EDIT_ITEM', 'Modifier l\'élément')
            : t('ADMIN', 'NEW_ITEM', 'Nouvel élément')}
        </h3>
        <form onSubmit={handleSubmitForm}>
          {formFields.map(field => (
            <div key={field.name} className={field.className || 'wa-form-group'}>
              <label htmlFor={field.name}>
                {t('ADMIN', field.labelKey, field.label)}
                {field.required && ' *'}
              </label>
              
              {field.type === 'dynamic-select' ? (
                <DynamicSelectField
                  fieldName={field.name}
                  value={formData[field.name] || ''}
                  onChange={handleFormChange}
                  required={field.required}
                  placeholder={field.placeholder}
                  apiEndpoint={apiEndpoint}
                  dynamicSource={field.dynamicSource}
                  fallbackOptions={field.fallbackOptions || []}
                />
              ) : field.type === 'select' ? (
                <select
                  id={field.name}
                  name={field.name}
                  value={formData[field.name] || ''}
                  onChange={handleFormChange}
                  required={field.required}
                >
                  {field.options.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              ) : field.type === 'textarea' ? (
                <textarea
                  id={field.name}
                  name={field.name}
                  value={formData[field.name] || ''}
                  onChange={handleFormChange}
                  required={field.required}
                  placeholder={field.placeholder}
                  rows={field.rows || 3}
                />
              ) : field.type === 'checkbox' || field.type === 'booleanTag' ? (
                <input
                  type="checkbox"
                  id={field.name}
                  name={field.name}
                  checked={formData[field.name] || false}
                  onChange={handleFormChange}
                />
              ) : (
                <input
                  type={field.type || 'text'}
                  id={field.name}
                  name={field.name}
                  value={formData[field.name] || ''}
                  onChange={handleFormChange}
                  required={field.required}
                  placeholder={field.placeholder}
                  min={field.min}
                  max={field.max}
                  step={field.step}
                />
              )}
            </div>
          ))}
          
          <div className="wa-form-actions">
            <Button type="submit" variant="success">
              {editingId 
                ? t('ADMIN', 'UPDATE', 'Mettre à jour') 
                : t('ADMIN', 'SAVE', 'Sauvegarder')}
            </Button>
            <Button 
              type="button" 
              variant="secondary"
              onClick={handleCancelForm}
            >
              {t('ADMIN', 'CANCEL', 'Annuler')}
            </Button>
          </div>
        </form>
      </div>
    );
  };

  // Rendu des filtres
  const renderFilters = () => {
    if (!hasFilters || filterDefinitions.length === 0) return null;

    // Fonction pour trouver le champ de formulaire correspondant pour obtenir la config dynamique
    const findCorrespondingFormField = (filterName) => {
      return formFields.find(field => 
        field.name === filterName && field.type === 'dynamic-select'
      );
    };

    return (
      <div className="wa-filters-container">
        <form className="wa-filters-form">
          <div className="wa-filter-row">
            {filterDefinitions.map(filter => {
              const correspondingFormField = findCorrespondingFormField(filter.name);
              
              return (
                <div key={filter.name} className="wa-filter-group">
                  <label htmlFor={filter.name}>
                    {t('ADMIN', filter.labelKey, filter.label)}
                  </label>
                  
                  {correspondingFormField ? (
                    // Utiliser le composant dynamique si le champ de formulaire correspondant est dynamique
                    <DynamicSelectField
                      fieldName={filter.name}
                      value={filters[filter.name] || ''}
                      onChange={handleFilterChange}
                      required={false}
                      placeholder={filter.placeholder || 'Tous'}
                      apiEndpoint={apiEndpoint}
                      dynamicSource={correspondingFormField.dynamicSource}
                    />
                  ) : filter.type === 'select' ? (
                    <select
                      id={filter.name}
                      name={filter.name}
                      value={filters[filter.name]}
                      onChange={handleFilterChange}
                    >
                    <option value="">Tous</option>
                    {filter.options.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                ) : filter.type === 'booleanTag' ? (
                  <div className="wa-checkbox-filter">
                    <input
                      type="checkbox"
                      id={filter.name}
                      name={filter.name}
                      checked={filters[filter.name] === '1'}
                      onChange={handleFilterChange}
                    />
                    <label htmlFor={filter.name} className="wa-checkbox-label">
                      {t('ADMIN', 'FILTER_ACTIVE_ONLY', 'Actifs seulement')}
                    </label>
                  </div>
                ) : (
                  <input
                    type={filter.type || 'text'}
                    id={filter.name}
                    name={filter.name}
                    value={filters[filter.name]}
                    onChange={handleFilterChange}
                    placeholder={filter.placeholder}
                    min={filter.min}
                    max={filter.max}
                  />
                )}
              </div>
              );
            })}
            
            {hasPagination && (
              <div className="wa-filter-group">
                <label htmlFor="limit">{t('ADMIN', 'FILTER_LIMIT', 'Limite')}</label>
                <input
                  type="number"
                  id="limit"
                  name="limit"
                  value={filters.limit}
                  onChange={handleFilterChange}
                  min="1"
                  max="1000"
                />
              </div>
            )}
          </div>
        </form>
      </div>
    );
  };

  // Pagination actuelle
  const currentPage = Math.floor(filters.offset / filters.limit) + 1;
  const totalPages = Math.ceil(totalCount / filters.limit);

  return (
    <div className="wa-admin-panel">
      <div className="wa-admin-header">
        <h2>{t('ADMIN', translations.title, `Gestion des ${entityName}`)}</h2>
        <div className="wa-admin-actions">
          <Button 
            variant="primary" 
            onClick={handleAddItem}
          >
            {t('ADMIN', 'ADD_ITEM', 'Ajouter un élément')}
          </Button>
          
          <Button 
            variant="secondary" 
            className="mr-2" 
            onClick={handleExportCsv}
          >
            {t('ADMIN', 'EXPORT_CSV', 'Exporter CSV')}
          </Button>
          
          <Button 
            variant="secondary" 
            onClick={handleExportJson}
          >
            {t('ADMIN', 'EXPORT_JSON', 'Exporter JSON')}
          </Button>
        </div>
      </div>

      {/* Formulaire d'ajout/édition */}
      {renderForm()}

      {/* Filtres */}
      {renderFilters()}

      {/* Message Log */}
      <MessageLog 
        key={message.key}
        type={message.type}
        text={message.text}
        autoHide={true}
        duration={5000}
      />

      {/* Erreur de chargement */}
      {error && (
        <div className="wa-error-message">
          {error}
        </div>
      )}

      {/* Tableau des données */}
      {loading ? (
        <div className="wa-loading-spinner"></div>
      ) : (
        <>
          {data.length === 0 ? (
            <div className="wa-no-results">
              {t('ADMIN', 'NO_RESULTS', 'Aucun élément trouvé avec les filtres actuels.')}
            </div>
          ) : (
            <>
              <div className="wa-table-container">
                <table className="wa-admin-table">
                  <thead>
                    <tr>
                      {columns.map(column => (
                        <th key={column.key}>
                          {column.labelIcon ? (
                            <span 
                              title={t('ADMIN', column.labelKey, column.label)}
                              className="wa-icon-header"
                            >
                              <FontAwesomeIcon icon={column.labelIcon} />
                            </span>
                          ) : (
                            t('ADMIN', column.labelKey, column.label)
                          )}
                        </th>
                      ))}
                      <th>{t('ADMIN', 'TABLE_ACTIONS', 'Actions')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.map(item => (
                      <tr key={item.id}>
                        {columns.map(column => (
                          <td key={column.key} className={column.className}>
                            {column.type === 'booleanTag' ? (
                              // Affichage d'un booleanTag avec icône toggle
                              renderBooleanToggle(item[column.key], column)
                            ) : column.render ? (
                              // Rendu personnalisé
                              column.render(item[column.key], item)
                            ) : (
                              // Affichage standard
                              item[column.key]
                            )}
                          </td>
                        ))}
                        <td className="wa-actions">
                          <Button 
                            variant="primary"
                            size="sm"
                            onClick={() => handleEditItem(item)}
                          >
                            {t('ADMIN', 'EDIT', 'Modifier')}
                          </Button>
                          <Button 
                            variant="danger"
                            size="sm"
                            onClick={() => handleDeleteItem(item.id)}
                          >
                            {t('ADMIN', 'DELETE', 'Supprimer')}
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              
              {/* Pagination */}
              {hasPagination && (
                <div className="wa-pagination">
                  <span>
                    {t('ADMIN', 'PAGINATION_INFO', 'Page {{current}} sur {{total}} ({{count}} éléments)', {
                      current: currentPage,
                      total: totalPages,
                      count: totalCount
                    })}
                  </span>
                  <div className="wa-pagination-controls">
                    <Button 
                      variant="secondary"
                      size="sm"
                      onClick={() => handlePageChange('prev')}
                      disabled={currentPage === 1}
                    >
                      {t('ADMIN', 'PAGINATION_PREV', 'Précédent')}
                    </Button>
                    <Button 
                      variant="secondary"
                      size="sm"
                      onClick={() => handlePageChange('next')}
                      disabled={currentPage === totalPages}
                    >
                      {t('ADMIN', 'PAGINATION_NEXT', 'Suivant')}
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
}
